import { formatWithThousandSeparators } from '@shared-core/utils/balances/balances'
import { Big } from '@shared-core/utils/balances/big'
import { Row } from '@shared-ui/components/ui/grid'
import { GText } from '@shared-ui/components/ui/text'
import { rh } from '@shared-ui/theme/scale'
import { useEffect, useRef, useState } from 'react'
import { Animated, Easing, type StyleProp, type ViewStyle } from 'react-native'

import { useCreateStyles } from './styles'

export type AppAnimatedBalanceProps = {
  balance: string
  balanceKey: string
  isCompact?: boolean
  style?: StyleProp<ViewStyle>
  testID?: string
}

export function AppAnimatedBalance({ balance, balanceKey, isCompact = false, style, testID }: AppAnimatedBalanceProps) {
  const styles = useCreateStyles()
  const animation = useRef(new Animated.Value(1)).current
  const previousKeyRef = useRef(balanceKey)
  const [displayedBalance, setDisplayedBalance] = useState(balance)
  const displayedBalanceRef = useRef(balance)

  // Суммы меняем без исчезновения текста; маску скрытия показываем через затухание.
  useEffect(() => {
    if (previousKeyRef.current === balanceKey) return
    previousKeyRef.current = balanceKey
    const previous = displayedBalanceRef.current
    const parseBalance = (value: string) => value.replace(/^\$/, '').replace(/\s/g, '').replace(',', '.')
    const isAmount = (value: string) => /^\$-?\d[\d\s]*(?:,\d+)?$/.test(value)

    if (isAmount(previous) && isAmount(balance)) {
      const start = Big(parseBalance(previous))
      const delta = Big(parseBalance(balance)).minus(start)
      const progress = new Animated.Value(0)
      const listener = progress.addListener(({ value }) => {
        const amount = start.plus(delta.times(String(value))).toFixed(2)
        const nextBalance = `$${formatWithThousandSeparators(amount).replace('.', ',')}`
        displayedBalanceRef.current = nextBalance
        setDisplayedBalance(nextBalance)
      })
      animation.setValue(1)
      const transition = Animated.timing(progress, {
        toValue: 1,
        duration: 260,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      })
      transition.start(({ finished }) => {
        if (!finished) return
        displayedBalanceRef.current = balance
        setDisplayedBalance(balance)
      })
      return () => {
        transition.stop()
        progress.removeListener(listener)
      }
    }

    const transition = Animated.timing(animation, { toValue: 0, duration: 80, useNativeDriver: true })
    transition.start(({ finished }) => {
      if (!finished) return
      displayedBalanceRef.current = balance
      setDisplayedBalance(balance)
      Animated.timing(animation, { toValue: 1, duration: 200, useNativeDriver: true }).start()
    })
    return () => {
      transition.stop()
      animation.stopAnimation()
    }
  }, [animation, balance, balanceKey])

  const content = isCompact ? (
    <GText fw={600} size={18}>
      {displayedBalance}
    </GText>
  ) : (
    <Row justify="center" align="flex-start" gap={4}>
      <GText fw={600} style={styles.balance}>
        {displayedBalance.split(',')[0]}
      </GText>
      {displayedBalance.includes(',') && (
        <GText fw={600} style={styles.balanceMinor}>
          {displayedBalance.split(',')[1]}
        </GText>
      )}
    </Row>
  )

  return (
    <Animated.View
      testID={testID}
      style={[
        {
          opacity: animation,
          transform: [{ translateY: animation.interpolate({ inputRange: [0, 1], outputRange: [rh(6), 0] }) }],
        },
        style,
      ]}
    >
      {content}
    </Animated.View>
  )
}
